import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";
import type { UseFormSetValue } from "react-hook-form";
import {
  type SearchServiceKey,
  type SearchModuleFormValues,
  DEFAULT_VEHICLE_TYPE,
  DEFAULT_PICKUP_TIME,
} from "@/components/search-form/search.modules.type";

export type SearchFormSnapshot = {
  locationId: string;
  search: string;
  lat?: number;
  lng?: number;
  rangeValue?: DateRange;
  singleDate?: Date;
  adults: number;
  children: number;
  rooms: number;
  vehicleType: string;
  pickupType: string;
  start: string;
  end: string;
  singleDateString: string;
  hasLocation: boolean;
};

export type SearchQuerySnapshot = {
  locationId: string;
  search: string;
  lat?: number;
  lng?: number;
  start: string;
  end: string;
  date: string;
  rangeValue?: DateRange;
  singleDateValue?: Date;
  adults: number;
  children: number;
  rooms: number;
  vehicleType: string;
  pickupType: string;
};

export type ServiceBeforeSubmitActions = {
  setValue: UseFormSetValue<SearchModuleFormValues>;
  setRange: (from?: Date, to?: Date) => Promise<unknown> | void;
  shouldSyncUrl: boolean;
};

export type ServiceTupleHandlers = {
  /** 1. Comparison tuples for change detection (reload icon): [formTuple, queryTuple] */
  getComparisonTuples: (
    form: SearchFormSnapshot,
    query: SearchQuerySnapshot,
  ) => readonly [readonly unknown[], readonly unknown[]];

  /** 2. Checks if location changed to decide newTab vs currentTab */
  isLocationChanged: (
    form: SearchFormSnapshot,
    query: SearchQuerySnapshot,
  ) => boolean;

  /** 3. Form draft reset tuples when opening a new tab: [fieldKey, queryValue][] */
  getResetEntries: (
    query: SearchQuerySnapshot,
  ) => ReadonlyArray<readonly [keyof SearchModuleFormValues, unknown]>;

  /** 4. Validation tuple: [isInvalid: boolean, errorField: string] */
  getValidationTuple: (
    form: SearchFormSnapshot,
  ) => readonly [isInvalid: boolean, errorField: string];

  /** 5. Optional pre-submit normalization (e.g. hotel checkout +1 day auto-fill) */
  onBeforeSubmit?: (
    form: SearchFormSnapshot,
    actions: ServiceBeforeSubmitActions,
  ) => void;
};

/**
 * 1. HOTEL SERVICE HANDLERS
 */
export const hotelHandlers: ServiceTupleHandlers = {
  getComparisonTuples: (form, query) => [
    [
      form.locationId,
      form.search,
      form.lat,
      form.lng,
      form.start,
      form.end,
      form.adults,
      form.children,
      form.rooms,
    ],
    [
      query.locationId,
      query.search,
      query.lat,
      query.lng,
      query.start,
      query.end,
      query.adults,
      query.children,
      query.rooms,
    ],
  ],

  isLocationChanged: (form, query) =>
    [
      form.locationId !== query.locationId,
      form.search !== query.search,
      form.lat !== query.lat,
      form.lng !== query.lng,
    ].some(Boolean),

  getResetEntries: (query) => [
    ["locationId", query.locationId],
    ["search", query.search],
    ["lat", query.lat],
    ["lng", query.lng],
    ["rangeValue", query.rangeValue],
    ["adults", query.adults],
    ["children", query.children],
    ["rooms", query.rooms],
  ],

  getValidationTuple: (form) => [
    !form.hasLocation && !form.search.trim(),
    "location",
  ],

  onBeforeSubmit: (form, { setValue, setRange, shouldSyncUrl }) => {
    if (
      form.rangeValue?.from &&
      (!form.rangeValue.to ||
        !dayjs(form.rangeValue.to).isAfter(dayjs(form.rangeValue.from), "day"))
    ) {
      const autoTo = dayjs(form.rangeValue.from).add(1, "day").toDate();
      setValue("rangeValue", { from: form.rangeValue.from, to: autoTo });
      if (shouldSyncUrl) void setRange(form.rangeValue.from, autoTo);
    }
  },
};

/**
 * 2. RENTAL SERVICE HANDLERS
 */
export const rentalHandlers: ServiceTupleHandlers = {
  getComparisonTuples: (form, query) => [
    [
      form.locationId,
      form.lat,
      form.lng,
      form.singleDateString,
      form.vehicleType,
      form.pickupType,
    ],
    [
      query.locationId,
      query.lat,
      query.lng,
      query.date,
      query.vehicleType,
      query.pickupType,
    ],
  ],

  isLocationChanged: (form, query) =>
    [
      form.locationId !== query.locationId,
      form.lat !== query.lat,
      form.lng !== query.lng,
    ].some(Boolean),

  getResetEntries: (query) => [
    ["locationId", query.locationId],
    ["lat", query.lat],
    ["lng", query.lng],
    ["singleDate", query.singleDateValue],
    ["vehicleType", query.vehicleType || DEFAULT_VEHICLE_TYPE],
    ["pickupType", query.pickupType || DEFAULT_PICKUP_TIME],
  ],

  getValidationTuple: (form) => {
    if (!form.hasLocation) return [true, "location"];
    if (!form.vehicleType) return [true, "vehicleType"];
    return [false, ""];
  },
};

/**
 *  3. TOUR & ACTIVITY SERVICE HANDLERS
 */
export const tourHandlers: ServiceTupleHandlers = {
  getComparisonTuples: (form, query) => [
    [form.locationId, form.search, form.lat, form.lng, form.singleDateString],
    [query.locationId, query.search, query.lat, query.lng, query.date],
  ],

  isLocationChanged: (form, query) =>
    [
      form.locationId !== query.locationId,
      form.search !== query.search,
      form.lat !== query.lat,
      form.lng !== query.lng,
    ].some(Boolean),

  getResetEntries: (query) => [
    ["locationId", query.locationId],
    ["search", query.search],
    ["lat", query.lat],
    ["lng", query.lng],
    ["singleDate", query.singleDateValue],
  ],

  getValidationTuple: () => [false, ""],
};

/**
 * 4. SPA & MASSAGE SERVICE HANDLERS
 */
export const spaHandlers: ServiceTupleHandlers = {
  getComparisonTuples: (form, query) => [
    [form.locationId, form.lat, form.lng, form.singleDateString],
    [query.locationId, query.lat, query.lng, query.date],
  ],

  isLocationChanged: (form, query) =>
    [
      form.locationId !== query.locationId,
      form.lat !== query.lat,
      form.lng !== query.lng,
    ].some(Boolean),

  getResetEntries: (query) => [
    ["locationId", query.locationId],
    ["lat", query.lat],
    ["lng", query.lng],
    ["singleDate", query.singleDateValue],
  ],

  getValidationTuple: (form) => [!form.hasLocation, "location"],
};

/**
 * 5. DISCOVER SERVICE HANDLERS
 */
export const discoverHandlers: ServiceTupleHandlers = {
  getComparisonTuples: () => [[], []],
  isLocationChanged: () => false,
  getResetEntries: () => [],
  getValidationTuple: () => [false, ""],
};

/**
 * 6. SERVICE HANDLERS REGISTRY (TUPLE/MAP LOOKUP - NO SWITCH CASE)
 */
export const SERVICE_HANDLERS: Record<SearchServiceKey, ServiceTupleHandlers> =
  {
    hotels: hotelHandlers,
    rentals: rentalHandlers,
    tour_act: tourHandlers,
    spa_msg: spaHandlers,
    discover: discoverHandlers,
  };

export const getServiceHandler = (
  key: SearchServiceKey,
): ServiceTupleHandlers => SERVICE_HANDLERS[key] ?? hotelHandlers;
